import React, { useEffect, useMemo } from "react";
import { Tooltip } from "@mui/material";
import {
  CreateGroupModal,
  DeleteUserGroupModal,
  UserViewTableModal,
} from "./UserGroupsModals";
import { useState } from "react";
import { Button, TextLink } from "@vds/buttons";
import {
  createNotificationGroupsEndPoint,
  deleteNotificationGroupsEndPoint,
  updateNotificationGroupsEndPoint,
} from "../../configuration";
import { Loader } from "@vds/loaders";
import AddGroupModal from "./AddGroupModal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteUserGroup } from "./userGroupsService";
import { hasPermission } from "../../global/utility/permissionUtils";
import CustomHOCTable from "../../global/components/Tables/CustomHOCTable";
import { Body, Title } from "@vds/typography";
import { getFilterOptions } from "../../global/utility/deviceTableUtils";
import { Icon } from "@vds/icons";
import { showNotificationBanner } from "../../store/actions";
import { useDispatch } from "react-redux";
import axiosInstance from "../../global/axiosInterceptor";
import { PAGE_IDENTIFIERS } from "../../global/constants/tableConstants";
import { USER_GROUP_DETAILS_CONSTANTS, DELETE_GROUP_MODAL_CONSTANTS, TABLE_COLUMNS } from "../../global/constants/constants";
import { sortAlphabetically, sortNumerically, sortByFormattedDate, SORT_DIRECTIONS } from "../../global/utility/sortingUtils";
import { setDetailNavSource } from "../../hooks/usePaginationReset";
import { getDateforUsers } from "../../global/utility/Utils";

const NotificationGroups = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const {
    notificationGroupList,
    loading,
    notificationUsersList,
    setRefreshGroupsTable,
    setShowNotificationPopup,
    handleAddGroupSuccess,
    setShowNotification,
    showNotification,
    bannerType,
    setBannerType,
  } = props;

  const defaultGroupFormFields = {
    name: "",
    id: "",
    groupemail: "",
    description: "",
    users: [],
    usersName: [],
  };
  const UserGroupsTable = CustomHOCTable();
  const [notificationDataSource, setNotificationDataSource] = useState(null);
  const [tableLoading, setTableLoading] = useState(loading);

  const [showUserInfo, setShowUserInfo] = useState({ isOpen: false });
  const [userInfo, setUserInfo] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState({
    isOpen: false,
    isLoading: false,
  });
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState({
    isOpen: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [searchGroup, setSearchGroup] = useState("");
  const [groupFormFields, setGroupFormFields] = useState(
    defaultGroupFormFields
  );
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const columns = useMemo(() => {
    return [
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            {USER_GROUP_DETAILS_CONSTANTS.COLUMN_GROUPS}
          </Title>
        ),
        dataIndex: "Groups",
        key: "Groups",
        showCheckBox: false,
        isFilter: true,
        width: "30%",
        filter: getFilterOptions(notificationDataSource, "Groups"),
        onFilter: (value, record) => record?.Groups?.includes(value?.trim()),
        sorter: (a, b) => sortAlphabetically(a, b, "Groups"),
        sortDirections: SORT_DIRECTIONS,
        render: (text, record) => {
          return (
            <TextLink
              type="standAlone"
              size="large"
              className="link notification-groups-name-link"
              data-testid="notificationGroups_textLinkGroupsButton"
              onClick={() => handleGroupInfo(record)}
            >
              <Body size="large" viewport="desktop" primitive="span">
                {text}
              </Body>
            </TextLink>
          );
        },
      },
      {
        title: (
          <Title
            size="small"
            bold
            primitive="h2"
            viewport="desktop"
            marginLeft="40px"
          >
            {USER_GROUP_DETAILS_CONSTANTS.COLUMN_MEMBERS}
          </Title>
        ),
        dataIndex: "Members",
        key: "Members",
        width: "20%",
        sorter: (a, b) => sortNumerically(a, b, "Members"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            {USER_GROUP_DETAILS_CONSTANTS.COLUMN_SITE}
          </Title>
        ),
        dataIndex: "Site",
        key: "Site",
        width: "20%",
        isFilter: true,
        filter: getFilterOptions(notificationDataSource, "Site"),
        onFilter: (value, record) => record?.Site?.includes(value?.trim()),
        sorter: (a, b) => sortAlphabetically(a, b, "Site"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            {USER_GROUP_DETAILS_CONSTANTS.COLUMN_DATE_CREATED}
          </Title>
        ),
        dataIndex: "DateAdded",
        key: "DateAdded",
        width: "15%",
        isFilter: true,
        filter: getFilterOptions(notificationDataSource, "DateAdded"),
        onFilter: (value, record) => record?.DateAdded?.includes(value?.trim()),
        sorter: (a, b) => sortByFormattedDate(a, b, "DateAdded"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            {TABLE_COLUMNS.ACTIONS}
          </Title>
        ),
        dataIndex: "Actions",
        key: "Actions",
        width: "15%",
        render: (text, record) => {
          return {
            children: (
              <div className="notification-groups-actions">
                <Tooltip title={USER_GROUP_DETAILS_CONSTANTS.TOOLTIP_DELETE_GROUP} arrow placement="top">
                  <div
                    className="notification-groups-delete-action"
                    data-testid="notificationGroups_trashbutton"
                    onClick={() => deleteUser(record.id, record)}
                  >
                    <Icon name="trash" size="medium" color="#000000" ariaHidden={true} />
                  </div>
                </Tooltip>
              </div>
            ),
          };
        },
      },
    ];
  }, [notificationDataSource]);

  const dynamicPostApiCall = async (endPoint, payload, method) => {
    const response = await axiosInstance[method?.toLowerCase() || "post"](
      endPoint(),
      {
        data: {
          ...payload,
        },
      }
    );
    return response;
  };

  const onSubmit = async (isUpdate) => {
    const { id, name, groupemail, description, users, usersName } =
      groupFormFields;
    setShowCreateGroup({ ...showCreateGroup, isLoading: true });
    let endPoint = createNotificationGroupsEndPoint;
    let method = "POST";
    const payload = {
      group: {
        name,
        groupemail,
        description,
      },
      userids: [...users.map((item) => item.id)],
    };

    if (isUpdate) {
      endPoint = updateNotificationGroupsEndPoint;
      payload["id"] = id;
      method = "PATCH";
    }

    const groupPostCallResponse = await dynamicPostApiCall(
      endPoint,
      payload,
      method
    );
    if (groupPostCallResponse?.ok) {
      setShowCreateGroup({ isOpen: false, isLoading: false });
      setRefreshGroupsTable(true);
    } else {
      setShowCreateGroup({ isOpen: false, isLoading: false });
      const error = await groupPostCallResponse.json();
      setShowNotificationPopup({
        message:
          groupPostCallResponse?.status === 500
            ? `${error?.error_description} 
            Possible reason(s) - Group may already Exist.`
            : "Something went wrong, Please try again later.",
        type: "error",
        show: true,
        title: "Something went wrong",
      });
    }
  };

  const handleAddGroupClick = () => {
    if (!hasPermission("User group management", "Create")) {
      setShowNotification(true);
      showPermissionsBanner();
    } else {
      setShowAddGroupModal(true);
    }
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_NO_PERMISSION,
      priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const handleGroupInputChange = (e, type, userRecord) => {
    const { name, value, checked } = e.currentTarget;
    if (type === "checkbox") {
      let users = [...groupFormFields.users];
      if (checked) {
        users = [...users, userRecord];
      } else {
        users = [...users.filter((item) => item.id !== value)];
      }
      console.log(users);
      setGroupFormFields((prev) => ({ ...prev, users }));
    } else {
      setGroupFormFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const confirmDelete = async (isConfirm, record) => {
    if (!isConfirm) {
      setShowDeleteGroupModal({ isOpen: false });
      return;
    }
    const group = notificationGroupList.find((g) => g.id === record);
    setShowDeleteGroupModal({ isOpen: false });
    setIsSaving(true);
    try {
      const response = await deleteUserGroup(record, activeSession);
      if (response?.status === 200 || response?.status === 204) {
        const notificationInfo = {
          type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_DELETED,
          priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_SUCCESS,
          data: {
            subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_DELETED_SUCCESS(group.name),
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
        setRefreshGroupsTable(true);
      } else {
        setRefreshGroupsTable(true);
        const notificationInfo = {
          type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_DELETED,
          priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
          data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_DELETED_FAILURE(group.name) },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    } catch (error) {
      setRefreshGroupsTable(true);
      const notificationInfo = {
        type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_DELETED,
        priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
        data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_DELETED_FAILURE(group.name) },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      setIsSaving(false);
    }
  };

  const setPermissionBanner = () => {
    setBannerType({ type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_NO_PERMISSION, priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL });
    setShowNotification(true);
  };

  const deleteUser = async (record, recordObj = {}) => {
    if (!hasPermission("User group management", "Delete")) {
      setPermissionBanner();
      showPermissionsBanner();
    } else {
      setShowDeleteGroupModal({
        isOpen: true,
        record: record,
        membersCount: recordObj?.Members,
        groupName: recordObj?.Groups,
      });
    }
  };

  const handleUserInfo = (record) => {
    setShowUserInfo({ isOpen: true });
    const users = record?.users ? [...record?.users] : [];
    setUserInfo([...users]);
  };

  const createGroup = (record, isGroupLink = false) => {
    if (record) {
      setShowCreateGroup({
        isOpen: true,
        mode: "edit",
        isLoading: false,
        isGroupLink,
      });
      setGroupFormFields({
        name: record?.name,
        id: record?.id,
        groupemail: record?.groupemail,
        description: record?.description,
        users: record?.users ? [...record?.users] : [],
        usersName: record?.users?.map((user) => user.name),
      });
    } else {
      setGroupFormFields(defaultGroupFormFields);
      setShowCreateGroup({ isOpen: true, isLoading: false });
    }
  };

  const handleGroupInfo = async (record) => {
    if (!hasPermission("User group management", "Edit")) {
      setShowNotification(true);
      showPermissionsBanner();
    } else {
      setDetailNavSource(PAGE_IDENTIFIERS.USER_GROUPS);
      navigate("/usergroupsdetails", { state: { response: record } });
    }
  };

  const getSiteDetails = (userItem) => {
    if (userItem?.tags?.length > 0) {
      const sortedTags = userItem.tags.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      const latestTag = sortedTags[0]?.name || "Not Available";
      return latestTag;
    }
    return "Not Available";
  };

  const getNotificationDataSource = (notificationGroupList) => {
    return notificationGroupList?.map((notificationGroupItem) => ({
      Groups: notificationGroupItem?.name,
      Members:
        notificationGroupItem?.users?.length > 0
          ? notificationGroupItem?.users?.length
          : USER_GROUP_DETAILS_CONSTANTS.TEXT_NA,
      Site: getSiteDetails(notificationGroupItem),
      DateAdded: notificationGroupItem?.createdon
        ? getDateforUsers(notificationGroupItem)
        : "",
      Actions: "",
      id: notificationGroupItem.id,
    }));
  };

  useEffect(() => {
    setTableLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!notificationGroupList?.length) {
      if (!loading && !tableLoading) setNotificationDataSource([]);
      return;
    }
    const notificationGroupData = getNotificationDataSource(notificationGroupList);
    setNotificationDataSource(notificationGroupData);
    setTableLoading(false);
  }, [notificationGroupList, loading, tableLoading]);

  const AddGroupButton = (
    <Button
      size="medium"
      disabled={false}
      use="primary"
      className="notification-groups-create-btn responsive-action-button"
      data-testid="notificationGroups_createGroupButton"
      onClick={handleAddGroupClick}
    >
      {USER_GROUP_DETAILS_CONSTANTS.CREATE_GROUP}
    </Button>
  );

  return (
    <>
      <AddGroupModal
        title={USER_GROUP_DETAILS_CONSTANTS.ADD_GROUP_TITLE}
        stepOneTitle={USER_GROUP_DETAILS_CONSTANTS.STEP_TWO_TITLE}
        stepTwoTitle={USER_GROUP_DETAILS_CONSTANTS.STEP_ONE_TITLE}
        stepTwoBody={USER_GROUP_DETAILS_CONSTANTS.STEP_TWO_BODY}
        stepThreeTitle={USER_GROUP_DETAILS_CONSTANTS.STEP_THREE_TITLE}
        stepThreeBody={USER_GROUP_DETAILS_CONSTANTS.STEP_THREE_BODY}
        stepFourTitle={USER_GROUP_DETAILS_CONSTANTS.STEP_FOUR_TITLE}
        showModal={showAddGroupModal}
        setShowModal={setShowAddGroupModal}
        onSuccess={handleAddGroupSuccess}
        setShowNotification={setShowNotification}
        bannerType={bannerType}
        setBannerType={setBannerType}
        notificationDataSource={notificationDataSource}
      />
      <Loader active={isSaving} fullscreen />
      <UserGroupsTable
        showSearch={true}
        extraAction={AddGroupButton}
        searchInputText={searchGroup}
        columns={columns}
        dataList={notificationDataSource}
        loading={tableLoading}
        pageIdentifier={PAGE_IDENTIFIERS.USER_GROUPS}
        useReduxPersistence={true}
      />
      <DeleteUserGroupModal
        showDeleteUserGroupModal={showDeleteGroupModal}
        confirmDelete={confirmDelete}
        title={DELETE_GROUP_MODAL_CONSTANTS.MODAL_TITLE}
        membersCount={showDeleteGroupModal?.membersCount}
        showWarningBanner={true}
      />
    </>
  );
};

export default NotificationGroups;
